import { useState, useContext, useEffect } from 'react';
import { UserContext } from '../../../contexts/userProvider';
import { PatrimonioContext } from '../../../contexts/patrimonioProvider';
import DataTable from '../../Table';
import LoadingSpinner from '../../Loader/load';
import PinConfirmation from '../Type/PinConfirmation';
import { cnpj as cnpjFmt } from 'cpf-cnpj-validator';
import { MOTIVOS } from '../../../constants/motivos';

const rowKey = (n) => `${n.nf}-${n.cpf_cnpj}`;

const Notas = () => {
    const { setAlert } = useContext(UserContext);
    const { listarNotasFiscais, excluirNotaFiscal } = useContext(PatrimonioContext);
    const [notas, setNotas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notaParaForcar, setNotaParaForcar] = useState(null);

    const carregar = async () => {
        setLoading(true);
        const dados = await listarNotasFiscais();
        setNotas(dados || []);
        setLoading(false);
    };

    useEffect(() => {
        carregar();
    }, []);

    const formatarData = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hh}:${mm}`;
    };

    const formatarCnpj = (valor) => {
        if (!valor) return '';
        try { return cnpjFmt.format(valor); } catch { return valor; }
    };

    const oferecerExclusaoForcada = (nota, qtdMovs) => {
        setAlert({
            type: 'confirm',
            visible: true,
            confirm: true,
            title: 'Exclusão Forçada',
            placeholder: `Esta NF possui ${qtdMovs} movimentação(ões) associada(s). A exclusão forçada removerá a NF, seus ${nota.qtd_patrimonios} patrimônio(s) e as ${qtdMovs} movimentação(ões). Esta operação é IRREVERSÍVEL. Deseja prosseguir?`,
            callback: () => {
                setNotaParaForcar(nota);
            }
        });
    };

    const handleExcluir = (rowId) => {
        const nota = notas.find(n => rowKey(n) === rowId);
        if (!nota) return;

        const qtdMovs = Number(nota.qtd_movimentacoes) || 0;

        if (qtdMovs > 0) {
            oferecerExclusaoForcada(nota, qtdMovs);
            return;
        }

        setAlert({
            type: 'confirm',
            visible: true,
            confirm: true,
            title: 'Excluir Nota Fiscal',
            placeholder: `Tem certeza que deseja excluir a NF ${nota.nf} (${nota.fornecedor_nome || formatarCnpj(nota.cpf_cnpj)})? Isso removerá ${nota.qtd_patrimonios} patrimônio(s). Esta ação não pode ser desfeita.`,
            callback: async () => {
                const result = await excluirNotaFiscal(nota.nf, nota.cpf_cnpj, false);
                if (result?.success) {
                    carregar();
                } else if (result?.motivo === MOTIVOS.MOVIMENTACOES_EXISTENTES) {
                    oferecerExclusaoForcada(nota, result.qtdMovs);
                } else if (result?.motivo === MOTIVOS.NADA_ENCONTRADO) {
                    // Lista estava desatualizada — resincroniza.
                    carregar();
                }
            }
        });
    };

    const handlePinConfirm = async () => {
        const nota = notaParaForcar;
        setNotaParaForcar(null);
        if (!nota) return;
        const result = await excluirNotaFiscal(nota.nf, nota.cpf_cnpj, true);
        if (result?.success) carregar();
    };

    const handlePinCancel = () => {
        setNotaParaForcar(null);
    };

    const headers = [
        { label: "Importação", key: "data_fmt", sortable: true },
        { label: "Nº NF", key: "nf", sortable: true },
        { label: "Fornecedor", key: "fornecedor_label", sortable: true },
        { label: "CNPJ", key: "cnpj_fmt", sortable: false },
        { label: "Patrimônios", key: "qtd_patrimonios", sortable: true },
        { label: "Movs.", key: "qtd_movimentacoes", sortable: true },
        { label: "Usuário", key: "usuario", sortable: true },
        { label: "Ações", key: "actions", sortable: false, actions: { "deletar": handleExcluir } }
    ];

    const dataFormatada = notas.map(n => ({
        ...n,
        id: rowKey(n),
        data_fmt: formatarData(n.data_importacao),
        cnpj_fmt: formatarCnpj(n.cpf_cnpj),
        fornecedor_label: n.fornecedor_nome || '—'
    }));

    return (
        <div className='entrada-container'>
            <div className='entrada-header flex items-center justify-between'>
                <h1>Notas Fiscais Importadas</h1>
                <button
                    onClick={carregar}
                    className='px-4 py-2 bg-vox-blue-600 hover:bg-vox-blue-700 text-white rounded-lg transition-colors duration-200'
                    data-tour-id="notas:atualizar"
                >
                    Atualizar
                </button>
            </div>
            <div className='entrada-body'>
                {loading ? (
                    <LoadingSpinner />
                ) : notas.length === 0 ? (
                    <div className='p-6 text-center text-gray-500 dark:text-gray-400'>
                        Nenhuma nota fiscal importada.
                    </div>
                ) : (
                    <DataTable headers={headers} data={dataFormatada} perPageItems={10} />
                )}
            </div>

            <PinConfirmation
                isOpen={!!notaParaForcar}
                onConfirm={handlePinConfirm}
                onCancel={handlePinCancel}
                title="Exclusão Forçada de NF"
                description="Digite o PIN de segurança para confirmar a exclusão da NF e todas as movimentações associadas"
                warningTitle="Operação Irreversível"
                warningText={notaParaForcar
                    ? `Serão removidos permanentemente: NF ${notaParaForcar.nf}, ${notaParaForcar.qtd_patrimonios} patrimônio(s) e ${notaParaForcar.qtd_movimentacoes} movimentação(ões).`
                    : 'Esta ação removerá NF, patrimônios e movimentações associadas.'}
            />
        </div>
    );
};

export default Notas;
